/**
 * Compact calendar widget with Gmail-style snooze
 * Shows upcoming cbCalendar events with quick actions
 */

import React, { useState, useEffect } from "react";
import {
  Button,
  Popover,
  List,
  Badge,
  Typography,
  Dropdown,
  Checkbox,
  DatePicker,
  Modal,
  message,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  TeamOutlined,
  MailOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Text, Link } = Typography;

interface CalendarEvent {
  id: string;
  subject: string;
  activitytype: string;
  date_start: string;
  time_start: string;
  eventstatus: string;
  rel_id?: string;
  account_name?: string;
}

// Activity type icons
const getActivityIcon = (type: string) => {
  switch (type?.toLowerCase()) {
    case "call":
      return <PhoneOutlined />;
    case "meeting":
      return <TeamOutlined />;
    case "email":
      return <MailOutlined />;
    default:
      return <CalendarOutlined />;
  }
};

// Snooze preset options
const getSnoozeOptions = (_event: CalendarEvent): MenuProps["items"] => {
  const now = dayjs();
  const nextWeek = now.add(7, "day");

  return [
    {
      key: "3h",
      label: `Later today (${now.add(3, "hour").format("h:mm A")})`,
    },
    {
      key: "tomorrow-am",
      label: `Tomorrow 9:00 AM`,
    },
    {
      key: "tomorrow-pm",
      label: `Tomorrow 2:00 PM`,
    },
    {
      key: "next-week",
      label: `Next week (${nextWeek.format("ddd, MMM D")})`,
    },
    { type: "divider" },
    {
      key: "pick",
      label: "Pick date & time...",
    },
  ];
};

export const CalendarWidget: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [pickDateEvent, setPickDateEvent] = useState<CalendarEvent | null>(
    null
  );
  const [pickDate, setPickDate] = useState<dayjs.Dayjs | null>(null);

  // Fetch upcoming events
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.post("/api/direct/query", {
        query: `SELECT id, subject, activitytype, date_start, time_start, eventstatus, rel_id
                FROM cbCalendar
                WHERE eventstatus != 'Held'
                LIMIT 10;`,
      });

      // API returns { success: true, result: [...] }
      const data = response.data?.result || response.data;
      if (Array.isArray(data) && data.length > 0) {
        // Fetch account names for events with rel_id
        const accountIds = [
          ...new Set(
            data
              .filter((e: CalendarEvent) => e.rel_id?.startsWith("11x"))
              .map((e: CalendarEvent) => e.rel_id)
          ),
        ];

        let accountMap: Record<string, string> = {};
        if (accountIds.length > 0) {
          const accountsResponse = await axios.post("/api/direct/query", {
            query: `SELECT id, accountname FROM Accounts WHERE id = '${accountIds.join(
              "' OR id = '"
            )}' LIMIT 100;`,
          });
          const accounts = accountsResponse.data?.result || [];
          accountMap = accounts.reduce(
            (
              acc: Record<string, string>,
              a: { id: string; accountname: string }
            ) => {
              acc[a.id] = a.accountname;
              return acc;
            },
            {}
          );
        }

        // Enrich events with account names
        const enrichedEvents = data.map((e: CalendarEvent) => ({
          ...e,
          account_name: e.rel_id ? accountMap[e.rel_id] : undefined,
        }));

        setEvents(enrichedEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Failed to fetch events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Mark event as complete
  const handleComplete = async (event: CalendarEvent) => {
    try {
      await axios.post("/api/direct/update", {
        id: event.id,
        data: { eventstatus: "Held" },
      });
      message.success("Event completed");
      fetchEvents();
    } catch (error) {
      message.error("Failed to update event");
    }
  };

  // Snooze event to new time
  const handleSnooze = async (event: CalendarEvent, key: string) => {
    if (key === "pick") {
      setPickDateEvent(event);
      return;
    }

    const now = dayjs();
    let newDate: dayjs.Dayjs;
    let newTime: string;

    switch (key) {
      case "3h":
        newDate = now.add(3, "hour");
        newTime = newDate.format("HH:mm:ss");
        break;
      case "tomorrow-am":
        newDate = now.add(1, "day").hour(9).minute(0);
        newTime = "09:00:00";
        break;
      case "tomorrow-pm":
        newDate = now.add(1, "day").hour(14).minute(0);
        newTime = "14:00:00";
        break;
      case "next-week":
        newDate = now.add(7, "day");
        newTime = event.time_start || "09:00:00";
        break;
      default:
        return;
    }

    try {
      await axios.post("/api/direct/update", {
        id: event.id,
        data: {
          date_start: newDate.format("YYYY-MM-DD"),
          time_start: newTime,
        },
      });
      message.success(`Snoozed to ${newDate.format("MMM D, h:mm A")}`);
      fetchEvents();
    } catch (error) {
      message.error("Failed to snooze event");
    }
  };

  // Handle custom date pick
  const handlePickDate = async () => {
    if (!pickDateEvent || !pickDate) return;

    try {
      await axios.post("/api/direct/update", {
        id: pickDateEvent.id,
        data: {
          date_start: pickDate.format("YYYY-MM-DD"),
          time_start: pickDate.format("HH:mm:ss"),
        },
      });
      message.success(`Rescheduled to ${pickDate.format("MMM D, h:mm A")}`);
      setPickDateEvent(null);
      setPickDate(null);
      fetchEvents();
    } catch (error) {
      message.error("Failed to reschedule event");
    }
  };

  // Format event time display
  const formatEventTime = (event: CalendarEvent) => {
    const eventDate = dayjs(event.date_start);
    const today = dayjs().startOf("day");
    const tomorrow = today.add(1, "day");

    let dateLabel = eventDate.format("MMM D");
    if (eventDate.isSame(today, "day")) dateLabel = "Today";
    else if (eventDate.isSame(tomorrow, "day")) dateLabel = "Tomorrow";

    const time = event.time_start
      ? dayjs(`${event.date_start} ${event.time_start}`).format("h:mm A")
      : "";

    return `${time} - ${dateLabel}`;
  };

  const content = (
    <div style={{ width: 320 }}>
      <List
        size="small"
        loading={loading}
        dataSource={events}
        locale={{ emptyText: "No upcoming events" }}
        renderItem={(event) => (
          <List.Item
            style={{ padding: "8px 0" }}
            actions={[
              <Checkbox
                key="complete"
                onChange={() => handleComplete(event)}
              />,
              <Dropdown
                key="snooze"
                menu={{
                  items: getSnoozeOptions(event),
                  onClick: ({ key }) => handleSnooze(event, key),
                }}
                trigger={["click"]}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<ClockCircleOutlined />}
                />
              </Dropdown>,
            ]}
          >
            <List.Item.Meta
              avatar={getActivityIcon(event.activitytype)}
              title={
                <Link
                  ellipsis
                  style={{ maxWidth: 180, display: "block" }}
                  onClick={() => {
                    setOpen(false);
                    const eventId = event.id?.split("x")[1] || event.id;
                    navigate(`/events/${eventId}`);
                  }}
                >
                  {event.subject}
                </Link>
              }
              description={
                <div style={{ fontSize: 11 }}>
                  <Text type="secondary">{formatEventTime(event)}</Text>
                  {event.account_name && (
                    <>
                      <br />
                      <Link
                        style={{ fontSize: 11 }}
                        onClick={() => {
                          setOpen(false);
                          // Extract numeric ID from WSID (e.g., "11x8995" -> "8995")
                          const accountId =
                            event.rel_id?.split("x")[1] || event.rel_id;
                          navigate(`/accounts/${accountId}`);
                        }}
                      >
                        {event.account_name}
                      </Link>
                    </>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        title="Upcoming Events"
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
      >
        <span>
          <Badge count={events.length} size="small" offset={[-2, 2]}>
            <Button type="text" icon={<CalendarOutlined />} />
          </Badge>
        </span>
      </Popover>

      <Modal
        title="Pick Date & Time"
        open={!!pickDateEvent}
        onOk={handlePickDate}
        onCancel={() => {
          setPickDateEvent(null);
          setPickDate(null);
        }}
        okText="Reschedule"
      >
        <DatePicker
          showTime
          value={pickDate}
          onChange={setPickDate}
          style={{ width: "100%" }}
        />
      </Modal>
    </>
  );
};
